import type { DateStr } from './dates';
import type { Stage } from './stages';

export const SOURCES = [
  'LinkedIn',
  'Glassdoor',
  'Telegram',
  'Email',
  'CompanySite',
  'InfoJobs',
  'Indeed',
  'Recruiter',
  'Talanto',
  'Himalayas',
  'Other',
] as const;
export type Source = (typeof SOURCES)[number];
export const SOURCE_LABELS: Record<Source, string> = {
  LinkedIn: 'LinkedIn',
  Glassdoor: 'Glassdoor',
  Telegram: 'Telegram',
  Email: 'Email',
  CompanySite: 'Сайт компании',
  InfoJobs: 'InfoJobs',
  Indeed: 'Indeed',
  Recruiter: 'Рекрутер',
  Talanto: 'Talanto',
  Himalayas: 'Himalayas',
  Other: 'Другое',
};

export const FORMATS = ['remote', 'hybrid', 'office'] as const;
export type Format = (typeof FORMATS)[number];
export const FORMAT_LABELS: Record<Format, string> = { remote: 'Удалёнка', hybrid: 'Гибрид', office: 'Офис' };

export const CHANNELS = ['LinkedIn', 'Email', 'Telegram', 'Call', 'Meeting', 'System', 'Other'] as const;
export type Channel = (typeof CHANNELS)[number];
export const CHANNEL_LABELS: Record<Channel, string> = {
  LinkedIn: 'LinkedIn',
  Email: 'Email',
  Telegram: 'Telegram',
  Call: 'Звонок',
  Meeting: 'Встреча',
  System: 'Событие',
  Other: 'Другое',
};

export const DIRECTIONS = ['in', 'out', 'sys'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const LETTER_LANGS = ['en', 'es', 'ru'] as const;
export type LetterLang = (typeof LETTER_LANGS)[number];
export const LANG_LABELS: Record<LetterLang, string> = { en: 'English', es: 'Español', ru: 'Русский' };

export const isOneOf = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);

/** Запись истории в том виде, в каком она нужна доменной логике. */
export interface CoreActivity {
  occurredOn: DateStr;
  direction: Direction;
  channel: Channel;
  text: string;
}

export interface CoreContact {
  name: string;
  role: string;
  email: string;
}

export interface CoreOffer {
  salaryK: number | null;
  bonus: string;
  format: Format | null;
  contract: string;
  startDate: DateStr | null;
  deadline: DateStr | null;
  benefits: string;
  gut: number | null;
}

/** Вакансия для доменной логики. История — в порядке добавления (дата, затем время создания). */
export interface CoreApp {
  id: string;
  company: string;
  role: string;
  source: Source | null;
  format: Format | null;
  location: string;
  salaryRange: string;
  appliedAt: DateStr | null;
  stage: Stage;
  maxStage: Stage;
  stageAt: DateStr | null;
  nextStep: string;
  nextDate: DateStr | null;
  nextTime: string | null;
  offer: CoreOffer | null;
  activities: CoreActivity[];
  contacts: CoreContact[];
}

export interface CoreSettings {
  ghostDays: number;
  minSalaryK: number | null;
  targetSalaryK: number | null;
  formats: Format[];
  preferredContract: string;
}

/**
 * Число из строки как в прототипе: «€78k», «78 000», «72,5» → тысячи.
 * Значения больше 1000 считаются указанными в евро и делятся на 1000.
 */
export function num(v: unknown): number {
  if (v == null || v === '') return 0;
  if (typeof v === 'number') return !isFinite(v) ? 0 : v > 1000 ? v / 1000 : v;
  const n = parseFloat(
    String(v)
      .replace(/[^\d.,-]/g, '')
      .replace(',', '.'),
  );
  if (!isFinite(n)) return 0;
  return n > 1000 ? n / 1000 : n;
}

/** Нормализация названия для сравнения компаний и ролей. */
export const normName = (x: string | null | undefined) =>
  (x ?? '').toLowerCase().replace(/[^a-zа-яё0-9]/gi, '');
